import { Injectable } from '@angular/core'
import {
  CheckInput,
  CheckOutput,
  ForgotPasswordInput,
  LoginInput,
  LoginOutput,
  PartnerGql,
  ResetPasswordInput,
  SignupInput,
  UpdateMeInput,
  UpdatePartnerInput,
  UserGql,
} from '@papi/interfaces'
import { Apollo, gql } from 'apollo-angular'
import { Observable, of } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import { appSchema, UsersPartnerApp } from '../apps/app.model'

export const PAPI_AUTH_TOKEN = 'papi-auth-token'

interface UsersPartner extends Pick<PartnerGql, '_id' | 'company'> {
  role: string
  apps: UsersPartnerApp[]
}

export interface AuthUser extends Pick<UserGql, '_id' | 'firstname' | 'lastname' | 'email' | 'imgId' | 'createdAt'> {
  partners: UsersPartner[]
}

const userSchema = `
{
  _id
  firstname
  lastname
  email
  imgId
  createdAt
  partners {
    _id
    company
    role
    apps ${appSchema}
  }
}`

const CHECKEMAIL_QUERY = gql`
  query ($input: CheckInput!) {
    check(input: $input) {
      exists
    }
  }
`

const ME_QUERY = gql`{ me ${userSchema} }`

const LOGIN_MUTATION = gql`
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      token
      user ${userSchema}
    }
  }
`

const SIGNUP_MUTATION = gql`
  mutation Signup($input: SignupInput!) {
    signup(input: $input) {
      success
    }
  }
`

const UPDATE_ME_MUTATION = gql`
  mutation UpdateMe($input: UpdateMeInput!) {
    updateMe(input: $input) {
      success
    }
  }
`

const UPDATE_PARTNER_MUTATION = gql`
  mutation UpdatePartner($input: UpdatePartnerInput!) {
    updatePartner(input: $input) {
      success
    }
  }
`
@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(private apollo: Apollo) {}

  autologin(): Observable<LoginOutput<AuthUser>> {
    const token = localStorage.getItem(PAPI_AUTH_TOKEN)
    if (!token) return of(null)
    this.storeToken(token)
    return this.apollo
      .query<{ me: AuthUser }>({ query: ME_QUERY })
      .pipe(map((res) => (res.data?.me ? { token, user: res.data.me } : null)))
  }

  login(input: LoginInput) {
    return this.apollo
      .mutate<{ login: LoginOutput<AuthUser> }>({ mutation: LOGIN_MUTATION, variables: { input } })
      .pipe(
        map((res) => res.data.login),
        tap((res) => this.storeToken(res?.token))
      )
  }

  signup(input: SignupInput) {
    return this.apollo.mutate<void>({ mutation: SIGNUP_MUTATION, variables: { input } })
  }

  checkEmail(input: CheckInput) {
    return this.apollo.query<{ check: CheckOutput }>({ query: CHECKEMAIL_QUERY, variables: { input } }).pipe(
      map((res) => res.data?.check.exists === false),
      catchError(() => of(false))
    )
  }

  forgot(input: ForgotPasswordInput) {
    return this.apollo.mutate<void>({
      mutation: gql`
        mutation ForgotPassword($input: ForgotPasswordInput!) {
          forgotPassword(input: $input) {
            success
          }
        }
      `,
      variables: { input },
    })
  }

  reset(input: ResetPasswordInput) {
    return this.apollo.mutate<void>({
      mutation: gql`
        mutation ResetPassword($input: ResetPasswordInput!) {
          resetPassword(input: $input) {
            success
          }
        }
      `,
      variables: { input },
    })
  }

  logout() {
    this.storeToken(null)
  }

  private storeToken(token: string) {
    if (token) {
      localStorage.setItem(PAPI_AUTH_TOKEN, token)
    } else {
      localStorage.removeItem(PAPI_AUTH_TOKEN)
    }
  }

  updateMe(input: UpdateMeInput) {
    return this.apollo.mutate({
      mutation: UPDATE_ME_MUTATION,
      variables: { input },
    })
  }

  updatePartner(input: UpdatePartnerInput) {
    return this.apollo.mutate({
      mutation: UPDATE_PARTNER_MUTATION,
      variables: { input },
    })
  }

  authorizationHeader() {
    return {
      Authorization: `Bearer ${localStorage.getItem(PAPI_AUTH_TOKEN)}`,
    }
  }
}
